import React, { useMemo, useCallback } from 'react';
import { GripVertical, Edit2, Trash2, Plus, Loader2, AlertTriangle } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { LocalQuestion, QuestionType, AssessmentType, FormType } from '../types';

const OBJECTIVE_TYPES = ['multiple_choice_single', 'multiple_choice_multi'];

interface QuestionsListProps {
  questions: LocalQuestion[];
  onAdd: () => void;
  onEdit: (question: LocalQuestion) => void;
  onDelete: (id: string) => void;
  onReorder?: (reordered: LocalQuestion[]) => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  saving: boolean;
  getTypeLabel: (type: QuestionType) => string;
  formType?: FormType;
  assessmentType?: AssessmentType;
  objectiveRatio?: number;
}

interface SortableQuestionItemProps {
  question: LocalQuestion;
  index: number;
  onEdit: (question: LocalQuestion) => void;
  onDelete: (id: string) => void;
  getTypeLabel: (type: QuestionType) => string;
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  question, index, onEdit, onDelete, getTypeLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="admin-feedback-question-item"
    >
      <div
        className="admin-feedback-question-drag"
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab' }}
      >
        <GripVertical size={16} />
        <span className="admin-feedback-question-number">Q{index + 1}</span>
      </div>
      <div className="admin-feedback-question-content">
        <div className="admin-feedback-question-text">{question.text}</div>
        <div className="admin-feedback-question-meta">
          <span className="admin-feedback-question-type">{getTypeLabel(question.type)}</span>
          {question.required && <span className="admin-feedback-question-required">Required</span>}
        </div>
      </div>
      <div className="admin-feedback-question-actions">
        <button className="admin-feedback-icon-btn" onClick={() => onEdit(question)}>
          <Edit2 size={16} />
        </button>
        <button
          className="admin-feedback-icon-btn admin-feedback-icon-btn-delete"
          onClick={() => onDelete(question.id)}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const QuestionsList: React.FC<QuestionsListProps> = ({
  questions,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
  onSaveDraft,
  onPublish,
  saving,
  getTypeLabel,
  formType,
  assessmentType,
  objectiveRatio = 50,
}) => {
  const showRatioStatus = formType === 'assessment' && assessmentType === 'both' && questions.length > 0;
  const objectiveCount = questions.filter(q => OBJECTIVE_TYPES.includes(q.type)).length;
  const subjectiveCount = questions.length - objectiveCount;
  const currentObjPercent = questions.length > 0 ? Math.round((objectiveCount / questions.length) * 100) : 0;
  const ratioDiff = Math.abs(currentObjPercent - objectiveRatio);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const questionIds = useMemo(() => questions.map(q => q.id), [questions]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...questions];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    onReorder?.(reordered);
  }, [questions, onReorder]);

  return (
    <div className="admin-feedback-questions-section">
      <div className="admin-feedback-questions-header">
        <h3>Questions ({questions.length})</h3>
        <button className="admin-feedback-add-question-btn" onClick={onAdd}>
          <Plus size={16} />
          Add Question
        </button>
      </div>

      {showRatioStatus && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '12px',
          borderRadius: '8px',
          border: `1px solid ${ratioDiff > 20 ? '#fde68a' : '#bbf7d0'}`,
          backgroundColor: ratioDiff > 20 ? '#fffbeb' : '#f0fdf4',
          fontSize: '13px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontWeight: 600, color: '#374151' }}>
              Question Ratio
            </span>
            <span style={{ color: '#6b7280' }}>
              Target: {objectiveRatio}% objective / {100 - objectiveRatio}% subjective
            </span>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <span style={{ color: '#1976d2', fontWeight: 500 }}>
              Objective: {objectiveCount} ({currentObjPercent}%)
            </span>
            <span style={{ color: '#9c27b0', fontWeight: 500 }}>
              Subjective: {subjectiveCount} ({100 - currentObjPercent}%)
            </span>
            {ratioDiff > 20 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#d97706', fontWeight: 500 }}>
                <AlertTriangle size={14} />
                Ratio is off by {ratioDiff}%
              </span>
            )}
          </div>
        </div>
      )}

      <div className="admin-feedback-questions-list">
        {questions.length === 0 ? (
          <div className="admin-feedback-empty-questions">
            No questions added yet. Click &quot;Add Question&quot; to start building your survey.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
              {questions.map((question, index) => (
                <SortableQuestionItem
                  key={question.id}
                  question={question}
                  index={index}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  getTypeLabel={getTypeLabel}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="admin-feedback-form-actions">
        <button
          className="admin-feedback-form-action-btn admin-feedback-form-action-btn-secondary"
          onClick={onSaveDraft}
          disabled={saving}
        >
          {saving ? <Loader2 size={16} className="admin-feedback-spinner" /> : null}
          Save as Draft
        </button>
        <button
          className="admin-feedback-form-action-btn admin-feedback-form-action-btn-primary"
          onClick={onPublish}
          disabled={saving}
        >
          {saving ? <Loader2 size={16} className="admin-feedback-spinner" /> : <span>&rsaquo;</span>}
          Publish Survey
        </button>
      </div>
    </div>
  );
};

export default React.memo(QuestionsList);
